import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Sidebar = ({ isOpen, closeSidebar }) => {
  const { currentUser } = useAuth();
  
  // Use onClick handler to close sidebar on mobile when a link is clicked
  const handleLinkClick = () => {
    if (window.innerWidth < 1024) { // lg breakpoint in Tailwind
      closeSidebar();
    }
  };

  // Close sidebar when escape key is pressed
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        closeSidebar();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, closeSidebar]);

  return (
    <aside 
      className={`fixed top-0 left-0 z-40 h-screen pt-5 transition-transform bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-lg
      ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
      lg:hidden w-64`}
      style={{ position: 'fixed', overflowY: 'auto', maxHeight: '100vh' }}
    >
      <div className="h-full px-3 pb-4 overflow-y-auto">
        <div className="flex justify-between items-center pb-4 mb-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 text-transparent bg-clip-text">GamePreBattle</h2>
          <button 
            onClick={closeSidebar}
            className="lg:hidden p-2 text-gray-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700"
            aria-label="Close sidebar"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>
          </button>
        </div>
        
        <ul className="space-y-2">
          <li>
            <NavLink 
              to="/" 
              className={({isActive}) => 
                `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
              }
              onClick={handleLinkClick}
              end
            >
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z"/>
              </svg>
              <span>Home</span>
            </NavLink>
          </li>
          
          <li>
            <NavLink 
              to="/matches" 
              className={({isActive}) => 
                `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
              }
              onClick={handleLinkClick}
            >
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm3.982 13.982a1 1 0 0 1-1.414 0l-3.274-3.274A1.012 1.012 0 0 1 9 10V6a1 1 0 0 1 2 0v3.586l2.982 2.982a1 1 0 0 1 0 1.414Z"/>
              </svg>
              <span>Matches</span>
            </NavLink>
          </li>
          
          <li>
            <NavLink 
              to="/wallet" 
              className={({isActive}) => 
                `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
              }
              onClick={handleLinkClick}
            >
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M18 5h-1V4a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2ZM4 3h10a1 1 0 0 1 1 1v1H4a1 1 0 0 1-1-1 1 1 0 0 1 1-1Zm11 16H4a1 1 0 0 1-1-1V6.83A2.99 2.99 0 0 0 4 7h11v12a1 1 0 0 1-1 1Z"/>
                <path d="M17 9h-3a1 1 0 0 0 0 2h3a1 1 0 0 0 0-2Z"/>
              </svg>
              <span>Wallet</span>
            </NavLink>
          </li>
          
          <li>
            <NavLink 
              to="/earn" 
              className={({isActive}) => 
                `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
              }
              onClick={handleLinkClick}
            >
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm.524 5.658a.5.5 0 0 1 .952 0l.883 2.716h2.857a.5.5 0 0 1 .294.905l-2.308 1.678.882 2.716a.5.5 0 0 1-.769.558L10 12.057l-2.315 1.681a.5.5 0 0 1-.769-.558l.882-2.716-2.308-1.678a.5.5 0 0 1 .294-.905h2.857l.883-2.716Z"/>
              </svg>
              <span>Earn Coins</span>
            </NavLink>
          </li>
          
          <li>
            <NavLink 
              to="/profile" 
              className={({isActive}) => 
                `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
              }
              onClick={handleLinkClick}
            >
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z"/>
              </svg>
              <span>Profile</span>
            </NavLink>
          </li>
          
          {currentUser && (
            <li>
              <NavLink 
                to="/referrals" 
                className={({isActive}) => 
                  `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
                }
                onClick={handleLinkClick}
              >
                <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M14.707 7.793a1 1 0 0 0-1.414 0L11 10.086V1.5a1 1 0 0 0-2 0v8.586L6.707 7.793a1 1 0 1 0-1.414 1.414l4 4a1 1 0 0 0 1.416 0l4-4a1 1 0 0 0-.002-1.414Z"/>
                  <path d="M18 12h-2.55l-2.975 2.975a3.5 3.5 0 0 1-4.95 0L4.55 12H2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2Zm-3 5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"/>
                </svg>
                <span>Referrals</span>
              </NavLink>
            </li>
          )}
          
          <li>
            <NavLink 
              to="/prediction-rules" 
              className={({isActive}) => 
                `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
              }
              onClick={handleLinkClick}
            >
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M18 7.5h-.423l-.452-1.09.3-.3a1.5 1.5 0 0 0 0-2.121L16.01 2.575a1.5 1.5 0 0 0-2.121 0l-.3.3-1.089-.452V2A1.5 1.5 0 0 0 11 .5H9A1.5 1.5 0 0 0 7.5 2v.423l-1.09.452-.3-.3a1.5 1.5 0 0 0-2.121 0L2.576 3.99a1.5 1.5 0 0 0 0 2.121l.3.3L2.423 7.5H2A1.5 1.5 0 0 0 .5 9v2A1.5 1.5 0 0 0 2 12.5h.423l.452 1.09-.3.3a1.5 1.5 0 0 0 0 2.121l1.415 1.413a1.5 1.5 0 0 0 2.121 0l.3-.3 1.09.452V18A1.5 1.5 0 0 0 9 19.5h2a1.5 1.5 0 0 0 1.5-1.5v-.423l1.09-.452.3.3a1.5 1.5 0 0 0 2.121 0l1.415-1.414a1.5 1.5 0 0 0 0-2.121l-.3-.3.452-1.09H18a1.5 1.5 0 0 0 1.5-1.5V9A1.5 1.5 0 0 0 18 7.5Zm-8 6a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Z"/>
              </svg>
              <span>Rules</span>
            </NavLink>
          </li>
          
          <li>
            <NavLink 
              to="/support" 
              className={({isActive}) => 
                `flex items-center p-3 text-gray-900 dark:text-white rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
              }
              onClick={handleLinkClick}
            >
              <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 me-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V7a1 1 0 0 1 2 0v4Z"/>
              </svg>
              <span>Support</span>
            </NavLink>
          </li>
        </ul>
        
        {currentUser && (
          <div className="pt-5 mt-5 border-t border-gray-200 dark:border-gray-700">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="h-8 w-8 rounded-full bg-blue-600 dark:bg-blue-500 text-white flex items-center justify-center font-semibold text-sm me-3">
                  {currentUser.displayName ? currentUser.displayName[0].toUpperCase() : 'U'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {currentUser.displayName || 'User'}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Player
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar; 